// ─────────────────────────────────────────────────────────────────────────────
// TmNext3, the Tour Manager's secondary strip (sits in the Biggest-movers slot,
// replacing it for the TM view). Two columns: "Next 3 · Tickets" (upcoming shows
// with live sold/cap counts) and "Top Merch · 30d" (reuses the dashboard's merch
// bestsellers). 1:1 port of the tm-next3 block in tour-manager-wiring.js.
// ─────────────────────────────────────────────────────────────────────────────

import type { JSX } from 'react';
import { useTourManagerSpotlight } from '../../lib';
import type { MerchBestseller } from '../../lib';

export interface TmNext3Props {
  /** Top merch bestsellers (30d) from the dashboard merch summary. */
  readonly bestsellers?: ReadonlyArray<MerchBestseller>;
}

export function TmNext3({ bestsellers = [] }: TmNext3Props): JSX.Element {
  const { data } = useTourManagerSpotlight();
  const shows = data.next3;
  const top = bestsellers.slice(0, 5);

  return (
    <section className="tm-next3" aria-label="Next shows and top merch">
      {/* Next 3 · Tickets */}
      <div className="tm-next3__col">
        <p className="tm-next3__label">Next 3 · Tickets</p>
        <ul className="tm-next3__shows">
          {shows.length === 0 ? (
            <li className="tm-next3__show tm-next3__show--empty">No upcoming shows yet.</li>
          ) : (
            shows.map((s) => (
              <li className="tm-next3__show" key={s.id || s.dayNum + s.venue}>
                <div className="tm-next3__date">
                  <span className="tm-next3__date-d">{s.dayNum}</span>
                  <span className="tm-next3__date-m">{s.monthAbbr}</span>
                </div>
                <div className="tm-next3__where">
                  <div className="tm-next3__venue">{s.venue}</div>
                  <div className="tm-next3__cap">{s.capLabel}</div>
                </div>
                <div className="tm-next3__sold">
                  <div className="tm-next3__sold-num">{s.soldLabel}</div>
                  <div className="tm-next3__sold-lbl">tickets</div>
                </div>
              </li>
            ))
          )}
        </ul>
      </div>

      {/* Top Merch · 30d */}
      <div className="tm-next3__col">
        <p className="tm-next3__label">Top Merch · 30d</p>
        <ol className="tm-next3__merch">
          {top.length === 0 ? (
            <li className="tm-next3__merch-row tm-next3__merch-row--empty">
              Connect Shopify or Square to surface top sellers
            </li>
          ) : (
            top.map((p, i) => (
              <li className="tm-next3__merch-row" key={String(p.product_id ?? i)}>
                <span className="tm-next3__merch-rank">{i + 1}</span>
                <span className="tm-next3__merch-title">
                  {p.title}{p.variant ? ' · ' + p.variant : ''}
                </span>
                <span className="tm-next3__merch-units">{p.units}</span>
              </li>
            ))
          )}
        </ol>
      </div>
    </section>
  );
}
